/* eslint-disable max-len */
import { DEFAULT_STATE, PRICE_RANGE } from '../constants'
import { searchPageStore } from '../index'

/**
 * Gets the current priceRanges value, always returns an array
 * @returns {string[]} - Array of price ranges, defaults to ["free"] if empty/undefined
 */
const getCurrentPriceRanges = () => {
  const state = searchPageStore.getState()
  const { priceRanges } = state

  if (!Array.isArray(priceRanges)) {
    return []
  }

  return priceRanges
}

/**
 * Sets the onSale filter
 * @param {boolean} onSale - Whether to filter for items on sale
 */
export const setOnSale = (onSale) => {
  searchPageStore.setState({ onSale })
}

/**
 * Sets the withSubscription filter
 * @param {boolean} withSubscription - Whether to filter for items with a subscription
 */
export const setWithSubscription = (withSubscription) => {
  if (withSubscription) {
    const currentPriceRanges = getCurrentPriceRanges()
    const filteredRanges = currentPriceRanges.filter((p) => p !== PRICE_RANGE.FREE)

    searchPageStore.setState({ withSubscription, priceRanges: filteredRanges })
    return
  }

  searchPageStore.setState({ withSubscription })
}

/**
 * Sets the price ranges filter
 * @param {string[]} priceRanges - Array with price range values
 */
export const setPriceRanges = (priceRanges) => {
  if (priceRanges.includes(PRICE_RANGE.FREE)) {
    searchPageStore.setState({
      priceRanges: [PRICE_RANGE.FREE],
      minPrice: DEFAULT_STATE.minPrice,
      maxPrice: DEFAULT_STATE.maxPrice,
      withSubscription: false,
    })
    return
  }

  searchPageStore.setState({ priceRanges })
}

/**
 * Adds a price range to the array
 * @param {string} priceRange - Price range to add
 */
export const addPriceRange = (priceRange) => {
  if (priceRange === PRICE_RANGE.FREE) {
    searchPageStore.setState({
      priceRanges: [PRICE_RANGE.FREE],
      minPrice: DEFAULT_STATE.minPrice,
      maxPrice: DEFAULT_STATE.maxPrice,
      withSubscription: false,
    })
    return
  }

  const currentPriceRanges = getCurrentPriceRanges()

  if (currentPriceRanges.length === 1 && currentPriceRanges[0] === PRICE_RANGE.FREE) {
    searchPageStore.setState({ priceRanges: [priceRange] })
    return
  }

  if (!currentPriceRanges.includes(priceRange)) {
    searchPageStore.setState({ priceRanges: [...currentPriceRanges, priceRange] })
  }
}

/**
 * Removes a price range from the array
 * @param {string} priceRange - Price range to remove
 */
export const removePriceRange = (priceRange) => {
  const currentPriceRanges = getCurrentPriceRanges()

  const newPriceRanges = currentPriceRanges.filter((p) => p !== priceRange)
  searchPageStore.setState({ priceRanges: newPriceRanges })
}

/**
 * Sets the minimum price filter
 * @param {number|string} minPrice - Minimum price value or empty string
 */
export const setMinPrice = (minPrice) => {
  const state = searchPageStore.getState()
  const currentPriceRanges = state.priceRanges || []

  const isFreePriceRangeSelected = currentPriceRanges.includes(PRICE_RANGE.FREE)
  if (isFreePriceRangeSelected) {
    const newPriceRanges = currentPriceRanges.filter((p) => p !== PRICE_RANGE.FREE)
    searchPageStore.setState({ minPrice, priceRanges: newPriceRanges })

    return
  }

  searchPageStore.setState({ minPrice })
}

/**
 * Sets the maximum price filter
 * @param {number|string} maxPrice - Maximum price value or empty string
 */
export const setMaxPrice = (maxPrice) => {
  const state = searchPageStore.getState()
  const currentPriceRanges = state.priceRanges || []

  const isFreePriceRangeSelected = currentPriceRanges.includes(PRICE_RANGE.FREE)
  if (isFreePriceRangeSelected) {
    const newPriceRanges = currentPriceRanges.filter((p) => p !== PRICE_RANGE.FREE)
    searchPageStore.setState({ maxPrice, priceRanges: newPriceRanges })

    return
  }

  searchPageStore.setState({ maxPrice })
}

/**
 * Clears all pricing filters
 */
export const clearPricing = () => {
  searchPageStore.setState({
    onSale: DEFAULT_STATE.onSale,
    withSubscription: DEFAULT_STATE.withSubscription,
    priceRanges: DEFAULT_STATE.priceRanges,
    minPrice: DEFAULT_STATE.minPrice,
    maxPrice: DEFAULT_STATE.maxPrice,
  })
}
